import React, { useState, useRef, useEffect, useCallback } from 'react';
import './index.css';
import { queryAgent, streamQueryWs, streamQuery, cancelStream, listConversations, getConversation, deleteConversation, renameConversation, submitMessageFeedback, isTokenValid, clearToken } from './api';
import TokenDashboard from './components/TokenDashboard';
import LoginModal from './components/LoginModal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist-min';

const Plot = createPlotlyComponent(Plotly);

// ============================================================================
// Chart Display Component
// ============================================================================

function ChartDisplay({ chartConfig }) {
  return (
    <div className="chart-container">
      <Plot
        data={chartConfig.data}
        layout={{ ...chartConfig.layout, autosize: true }}
        config={{ responsive: true, displayModeBar: true, displaylogo: false }}
        style={{ width: '100%' }}
        useResizeHandler={true}
      />
    </div>
  );
}

// ============================================================================
// Streamed Turn — the answer as it is being written
// ============================================================================

/**
 * Renders a streamed turn in arrival order: narration paragraphs, the tool calls that
 * produced them, and any chart, interleaved as they actually happened.
 *
 * Beats are memoized per index so a token arriving in the newest beat does not force
 * react-markdown to reparse every earlier paragraph.
 */
const StreamedBeat = React.memo(function StreamedBeat({ text }) {
  return (
    <div className="answer">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
});

function StreamedTurn({ items }) {
  return (
    <>
      {items.map((item, i) => {
        if (item.kind === 'beat') {
          return item.text ? <StreamedBeat key={`beat-${item.index}`} text={item.text} /> : null;
        }
        if (item.kind === 'progress') {
          return <div className="stream-progress" key={`p-${i}`}>{item.text}</div>;
        }
        if (item.kind === 'tool') {
          const status = item.running ? 'stream-tool--running'
            : item.success === false ? 'stream-tool--failed' : '';
          return (
            <div className={`stream-tool ${status}`} key={`t-${i}`} title={item.name}>
              <span className="stream-tool-name">{item.label || item.name}</span>
              {!item.running && typeof item.ms === 'number' && (
                <span className="stream-tool-time">{(item.ms / 1000).toFixed(1)}s</span>
              )}
            </div>
          );
        }
        if (item.kind === 'chart') {
          return <ChartDisplay key={`c-${i}`} chartConfig={item.config} />;
        }
        if (item.kind === 'gap') {
          return (
            <div className="stream-progress" key={`g-${i}`}>
              Reconnected — some updates were missed. The final answer below is complete.
            </div>
          );
        }
        return null;
      })}
    </>
  );
}

/** True when a streamed turn produced narration BEATS — only then is the timeline the
 *  answer. Progress lines/chips alone (template mode) are chrome around a formatter
 *  answer that must still be rendered. */
function hasStreamedContent(turn) {
  return Boolean(turn.stream?.items?.some(it => it.kind === 'beat' && it.text));
}

const normalizePara = (s) => (s || '').replace(/\s+/g, ' ').trim();

/**
 * Rebuild stream items from a persisted metadata.stream_timeline, so a reloaded
 * conversation shows the same interleaved trail the user watched live. The chart config
 * is spliced back in at its recorded position (the timeline stores only a marker).
 */
function timelineToItems(timeline, chartConfig) {
  if (!Array.isArray(timeline) || !timeline.length) return [];
  let beatIndex = 0;
  const items = [];
  for (const entry of timeline) {
    if (entry.type === 'beat' && entry.text) {
      beatIndex += 1;
      items.push({ kind: 'beat', index: beatIndex, text: entry.text });
    } else if (entry.type === 'step') {
      items.push({
        kind: 'tool', name: entry.name, toolKind: entry.kind,
        label: entry.label || entry.name, running: false,
        ms: entry.execution_time_ms, success: entry.success,
      });
    } else if (entry.type === 'chart' && chartConfig) {
      items.push({ kind: 'chart', config: chartConfig });
    } else if (entry.type === 'progress' && entry.text) {
      items.push({ kind: 'progress', text: entry.text });
    }
  }
  return items;
}

/**
 * Reconcile the live timeline with the authoritative final answer: the backend
 * deduplicates near-identical beats (a final beat restating an interim one), so any
 * beat that was dropped server-side must disappear from the view too — otherwise the
 * user reads the same sentence twice. Chips and charts keep their positions.
 *
 * The kept set comes from metadata.stream_timeline — exact whole-beat texts. Never
 * derive it by splitting `answer` on blank lines: a multi-paragraph beat (a full
 * project list) matches none of its own fragments and would vanish at finish.
 */
function reconcileItems(items, answer, timeline) {
  if (!items?.length || !answer) return items;

  const timelineBeats = (Array.isArray(timeline) ? timeline : [])
    .filter(e => e.type === 'beat' && e.text)
    .map(e => normalizePara(e.text));

  let pruned;
  if (timelineBeats.length) {
    const kept = new Set(timelineBeats);
    pruned = items.filter(it => it.kind !== 'beat' || kept.has(normalizePara(it.text)));
  } else {
    // Older backend without a timeline: keep any beat still contained in the answer.
    const whole = normalizePara(answer);
    pruned = items.filter(it => it.kind !== 'beat' || whole.includes(normalizePara(it.text)));
  }

  const hadBeats = items.some(it => it.kind === 'beat');
  const stillHasBeats = pruned.some(it => it.kind === 'beat');
  // Paranoia: if no beat survived matching (answer reformatted beyond recognition),
  // keep the stream as-is rather than blanking the turn.
  return hadBeats && !stillHasBeats ? items : pruned;
}

// ============================================================================
// Tool Details Components
// ============================================================================

function ToolDetailCard({ tool }) {
  const [showArgs, setShowArgs] = useState(false);
  const [showResult, setShowResult] = useState(false);

  return (
    <div className="tool-card">
      <div className="tool-card-header">
        <span className="tool-name">{tool.name}</span>
        <span className="tool-time-badge">{tool.execution_time_ms}ms</span>
      </div>
      <div className="tool-card-body">
        <div className="tool-section">
          <button className="section-toggle" onClick={() => setShowArgs(!showArgs)}>
            {showArgs ? '▼' : '▶'} Arguments
          </button>
          {showArgs && (
            <pre className="json-viewer">{JSON.stringify(tool.args, null, 2)}</pre>
          )}
        </div>
        {tool.formatted_query && (
          <div className="tool-section">
            <div className="section-label">OpenSearch Query DSL:</div>
            <pre className="json-viewer query-highlight">{tool.formatted_query}</pre>
          </div>
        )}
        <div className="tool-section">
          <button className="section-toggle" onClick={() => setShowResult(!showResult)}>
            {showResult ? '▼' : '▶'} Result Preview
          </button>
          {showResult && (
            <div className="result-preview">{tool.result_preview}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ToolDetailsExpander({ toolDetails }) {
  const [expanded, setExpanded] = useState(false);
  if (!toolDetails || toolDetails.length === 0) return null;
  return (
    <div className="tool-details-section">
      <button className="tool-expander-button" onClick={() => setExpanded(!expanded)}>
        {expanded ? '▼' : '▶'} Show Tool Details ({toolDetails.length} tool{toolDetails.length !== 1 ? 's' : ''} used)
      </button>
      {expanded && (
        <div className="tool-details-content">
          {toolDetails.map((tool, index) => (
            <ToolDetailCard key={index} tool={tool} />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Conversation Turn
// ============================================================================

function ConversationTurn({ turn, onSubmitFeedback }) {
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');

  const messageId = turn.result?.metadata?.message_id;
  const feedback = turn.result?.feedback || null;
  const feedbackRating = feedback?.rating || null;
  const hasFeedbackError = Boolean(turn.feedbackError);
  const canRate = Boolean(turn.result?.success && messageId && !turn.loading);

  const sendFeedback = async (rating, comment = null) => {
    if (!canRate || !onSubmitFeedback) return;
    await onSubmitFeedback(turn.id, { rating, comment });
  };

  const handleThumbDownClick = async () => {
    if (feedbackRating === 'down') {
      setCommentOpen(v => !v);
      if (!commentOpen) {
        setCommentDraft(feedback?.comment || '');
      }
      return;
    }
    await sendFeedback('down', null);
    setCommentOpen(true);
    setCommentDraft('');
  };

  const handleCommentSave = async () => {
    await sendFeedback('down', commentDraft);
    setCommentOpen(false);
  };

  return (
    <div className="conversation-turn">
      {/* User bubble */}
      <div className="user-bubble-wrapper">
        <div className="user-bubble">{turn.question}</div>
      </div>

      {/* Agent: in progress — narration, tool calls and charts as they arrive */}
      {turn.loading && (
        <div className="agent-bubble-wrapper">
          <div className="agent-bubble">
            {turn.stream?.items?.length > 0 && <StreamedTurn items={turn.stream.items} />}
            <div className="agent-loading">
              <div className="spinner"></div>
              <span>{turn.stream?.progress || 'Agent is thinking...'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Agent: error */}
      {turn.error && !turn.loading && (
        <div className="agent-bubble-wrapper">
          <div className="agent-bubble">
            <div className="error">
              <p><strong>Error:</strong> {turn.error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Agent: answer */}
      {turn.result && !turn.loading && (
        <div className="agent-bubble-wrapper">
          <div className="agent-bubble">
            {turn.result.success ? (
              <>
                {/* A streamed turn keeps what the user actually read — result.answer is the
                    same narration concatenated, so rendering both would duplicate it.
                    History reloads and the non-streaming path have no items and fall back. */}
                {hasStreamedContent(turn) ? (
                  <StreamedTurn items={turn.stream.items} />
                ) : (
                  <div className="answer">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {turn.result.answer}
                    </ReactMarkdown>
                  </div>
                )}
                {canRate && (
                  <div className="feedback-bar">
                    <button
                      type="button"
                      className={`feedback-btn ${feedbackRating === 'up' ? 'feedback-btn--active' : ''}`}
                      onClick={() => sendFeedback('up', feedback?.comment || null)}
                      disabled={turn.feedbackLoading}
                      title="Helpful answer"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      className={`feedback-btn ${feedbackRating === 'down' ? 'feedback-btn--active' : ''}`}
                      onClick={handleThumbDownClick}
                      disabled={turn.feedbackLoading}
                      title="Needs improvement"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L10.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"/>
                      </svg>
                    </button>
                    {turn.feedbackLoading && <span className="feedback-status">Saving…</span>}
                    {!turn.feedbackLoading && feedback?.submitted_at && (
                      <span className="feedback-status">Saved</span>
                    )}
                  </div>
                )}
                {canRate && feedbackRating === 'down' && (commentOpen || feedback?.comment) && (
                  <div className="feedback-comment-wrap">
                    <textarea
                      className="feedback-comment-input"
                      placeholder="Optional: what was wrong or missing?"
                      value={commentOpen ? commentDraft : (feedback?.comment || '')}
                      onChange={e => setCommentDraft(e.target.value)}
                      maxLength={1000}
                      disabled={turn.feedbackLoading || !commentOpen}
                    />
                    {commentOpen && (
                      <div className="feedback-comment-actions">
                        <button
                          type="button"
                          className="feedback-action-btn"
                          onClick={handleCommentSave}
                          disabled={turn.feedbackLoading}
                        >
                          Save comment
                        </button>
                        <button
                          type="button"
                          className="feedback-action-btn feedback-action-btn--ghost"
                          onClick={() => {
                            setCommentOpen(false);
                            setCommentDraft(feedback?.comment || '');
                          }}
                          disabled={turn.feedbackLoading}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {turn.result.metadata && (
                  <div className="metadata">
                    <small>
                      {turn.result.metadata.total_execution_time_ms && (
                        <>⏱️ {(turn.result.metadata.total_execution_time_ms / 1000).toFixed(2)}s{' | '}</>
                      )}
                      Tools: {turn.result.metadata.tool_calls || 0}
                      {' | '}Messages: {turn.result.metadata.message_count || 0}
                      {turn.result.metadata.token_usage && (
                        <>{' | '}Tokens: {turn.result.metadata.token_usage.total_tokens}</>
                      )}
                    </small>
                  </div>
                )}
                {/* Skip when the stream already rendered the chart inline, or it appears twice. */}
                {turn.result.metadata?.chart_config &&
                  !turn.stream?.items?.some(it => it.kind === 'chart') && (
                  <ChartDisplay chartConfig={turn.result.metadata.chart_config} />
                )}
                {turn.result.metadata?.tool_details && (
                  <ToolDetailsExpander toolDetails={turn.result.metadata.tool_details} />
                )}
                {hasFeedbackError && (
                  <div className="feedback-error">
                    Could not save feedback: {turn.feedbackError}
                  </div>
                )}
              </>
            ) : (
              <div className="error">
                <p><strong>Failed to process query:</strong> {turn.result.error}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Welcome Screen (shown before first message)
// ============================================================================

function WelcomeScreen() {
  return (
    <div className="welcome-screen">
      <div className="welcome-icon">🔍</div>
      <h2 className="welcome-title">OpenSearch Natural Language Agent</h2>
      <p className="welcome-subtitle">
        Ask questions about your data in plain English. The agent will query OpenSearch and explain the results.
      </p>
      <div className="welcome-features">
        <div className="feature-chip">🤖 LangGraph Agent</div>
        <div className="feature-chip">🧠 GPT powered</div>
        <div className="feature-chip">💬 Conversation Memory</div>
        <div className="feature-chip">🔍 OpenSearch</div>
      </div>
    </div>
  );
}

// ============================================================================
// Conversation Sidebar
// ============================================================================

function relativeTime(isoString) {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(isoString).toLocaleDateString();
}

function ConversationSidebar({ open, conversations, activeId, onSelect, onDelete, onRename, onNew, loading }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');

  const startEdit = (conv) => {
    setEditingId(conv.session_id);
    setDraft(conv.title || '');
  };

  const commitEdit = async () => {
    const title = draft.trim();
    const id = editingId;
    setEditingId(null);
    if (id && title) await onRename(id, title);
  };

  return (
    <aside className={`conv-sidebar ${open ? 'conv-sidebar--open' : ''}`}>
      <div className="conv-sidebar-header">
        <span className="conv-sidebar-title">History</span>
        <button className="conv-new-btn" onClick={onNew} title="New conversation">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New
        </button>
      </div>

      <div className="conv-list">
        {conversations.length === 0 && !loading && (
          <p className="conv-empty">No past conversations yet.</p>
        )}
        {conversations.map(conv => (
          <div
            key={conv.session_id}
            className={`conv-item ${conv.session_id === activeId ? 'conv-item--active' : ''}`}
            onClick={() => editingId !== conv.session_id && onSelect(conv.session_id)}
          >
            <div className="conv-item-body">
              {editingId === conv.session_id ? (
                <input
                  className="conv-item-edit"
                  value={draft}
                  maxLength={120}
                  autoFocus
                  onChange={e => setDraft(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  onBlur={commitEdit}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitEdit();
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                />
              ) : (
                <span className="conv-item-title">{conv.title || '(untitled)'}</span>
              )}
              <span className="conv-item-time">{relativeTime(conv.last_updated)}</span>
            </div>
            <button
              className="conv-item-rename"
              title="Rename"
              onClick={e => { e.stopPropagation(); startEdit(conv); }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
              </svg>
            </button>
            <button
              className="conv-item-delete"
              title="Delete"
              onClick={e => { e.stopPropagation(); onDelete(conv.session_id); }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14H6L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4h6v2" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </aside>
  );
}

// ============================================================================
// Main App
// ============================================================================

function App() {
  const [showLogin, setShowLogin] = useState(() => !isTokenValid());

  const [activeTab, setActiveTab] = useState('query');
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [turns, setTurns] = useState([]);

  // Agent settings
  const [evaluatorEnabled, setEvaluatorEnabled] = useState(false);
  const [maxRetries, setMaxRetries] = useState(1);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [convLoading, setConvLoading] = useState(false);

  /** Sent only to POST /api/query — must stay the short id (UUID), not the server storage key. */
  const clientSessionId = useRef(crypto.randomUUID());
  /** Full key from metadata / list; used for feedback and GET/DELETE /api/conversations/{id}. */
  const storageSessionId = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const chipsRef = useRef(null);
  /** In-flight stream: the fetch controller plus the server run id, for the stop button. */
  const streamAbort = useRef({ controller: null, runId: null });

  const scrollChips = (dir) => {
    if (chipsRef.current) {
      chipsRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
    }
  };

  const exampleQuestions = [
    "List all available projects",
    "Which projects have the most tests?",
    "Which are the top 10 most used materials across formulations?",
    "List experiments for each project",
    "Pick any example project and show its experiments and results",
  ];

  const requireAuth = useCallback(() => {
    if (!isTokenValid()) {
      clearToken();
      setShowLogin(true);
      return false;
    }
    return true;
  }, []);

  // Fetch the conversation list from the backend
  const refreshConversations = useCallback(async () => {
    if (!requireAuth()) return;
    try {
      const list = await listConversations();
      setConversations(list);
    } catch (err) {
      if (err.message?.includes('401')) { clearToken(); setShowLogin(true); }
      // non-fatal: sidebar may show stale data if backend is unavailable
    }
  }, [requireAuth]);

  // Load sidebar conversations when it first opens
  useEffect(() => {
    if (sidebarOpen) {
      refreshConversations();
    }
  }, [sidebarOpen, refreshConversations]);

  // Follow the conversation as it grows — but only when the user is already near the
  // bottom. Streaming updates state many times per answer, and unconditionally scrolling
  // would yank the view away from anyone who scrolled up to read something.
  useEffect(() => {
    const container = messagesEndRef.current?.parentElement;
    if (container) {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom > 200) return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns]);

  // Auto-resize textarea as user types
  const handleTextareaChange = (e) => {
    setQuestion(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 140) + 'px';
  };

  const resetTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const submitQuestion = async (q) => {
    if (!q.trim() || loading) return;
    if (!requireAuth()) return;
    const turnId = Date.now();
    setLoading(true);
    setQuestion('');
    resetTextarea();
    setTurns(prev => [...prev, {
      id: turnId, question: q, loading: true, result: null, error: null,
      stream: { runId: null, items: [], progress: null, narrationEnabled: false },
    }]);

    const options = {
      evaluate: evaluatorEnabled,
      allowRetry: evaluatorEnabled && maxRetries > 0,
      maxRetries: evaluatorEnabled ? maxRetries : 0,
    };

    const finish = (result) => {
      const scoped = result?.metadata?.storage_session_id;
      if (scoped) {
        storageSessionId.current = scoped;
      }
      // Align the live timeline with the authoritative (deduplicated) answer.
      const reconciled = {
        ...stream,
        items: reconcileItems(stream.items, result?.answer, result?.metadata?.stream_timeline),
      };
      setTurns(prev => prev.map(t =>
        t.id === turnId ? { ...t, loading: false, result, stream: reconciled } : t
      ));
      refreshConversations();
    };

    // Tokens arrive far faster than React should re-render. Accumulate into a local
    // buffer and flush on a timer, so <ReactMarkdown> reparses ~15×/s rather than per token.
    const pending = { items: null, progress: null, runId: null, narrationEnabled: null };
    let flushTimer = null;
    const stream = { runId: null, items: [], progress: null, narrationEnabled: false };

    const flush = () => {
      flushTimer = null;
      const snapshot = {
        runId: pending.runId ?? stream.runId,
        items: pending.items ?? stream.items,
        progress: pending.progress ?? stream.progress,
        narrationEnabled: pending.narrationEnabled ?? stream.narrationEnabled,
      };
      pending.items = pending.progress = pending.runId = pending.narrationEnabled = null;
      setTurns(prev => prev.map(t => t.id === turnId ? { ...t, stream: snapshot } : t));
    };
    const scheduleFlush = () => {
      if (flushTimer === null) flushTimer = setTimeout(flush, 66);
    };

    const onEvent = (event) => {
      switch (event.type) {
        case 'run_started':
          stream.runId = event.run_id;
          stream.narrationEnabled = Boolean(event.narration_enabled);
          pending.runId = stream.runId;
          pending.narrationEnabled = stream.narrationEnabled;
          streamAbort.current.runId = event.run_id;
          break;
        case 'progress':
          // With the narrator active these are transient scaffolding, so only the latest
          // is shown. In template mode they ARE the narration, so they are kept in order.
          if (stream.narrationEnabled) {
            stream.progress = event.text;
            pending.progress = event.text;
          } else {
            stream.items = [...stream.items, { kind: 'progress', text: event.text }];
            pending.items = stream.items;
          }
          break;
        case 'tool_started':
          stream.items = [...stream.items, {
            kind: 'tool', name: event.name, toolKind: event.kind,
            label: event.label || event.name, running: true,
          }];
          pending.items = stream.items;
          break;
        case 'tool_finished': {
          // Settle the matching running chip (label may change: "Searching…" → "Searched",
          // or carry the outcome in template mode). Older backends never send tool_started,
          // so fall back to appending.
          let ti = -1;
          for (let k = stream.items.length - 1; k >= 0; k -= 1) {
            const it = stream.items[k];
            if (it.kind === 'tool' && it.running && it.name === event.name) { ti = k; break; }
          }
          const settled = {
            kind: 'tool', name: event.name, toolKind: event.kind,
            label: event.label || event.name, running: false,
            ms: event.execution_time_ms, success: event.success,
          };
          if (ti === -1) {
            stream.items = [...stream.items, settled];
          } else {
            const copy = [...stream.items];
            copy[ti] = settled;
            stream.items = copy;
          }
          pending.items = stream.items;
          break;
        }
        case 'beat_started':
          stream.items = [...stream.items, { kind: 'beat', index: event.beat_index, text: '' }];
          pending.items = stream.items;
          break;
        case 'token': {
          // Append to the beat this token belongs to (never blindly to the last item —
          // a tool card can land between two beats). Hand-rolled reverse scan rather than
          // findLastIndex, which is ES2023 and would not be polyfilled by the CRA build.
          let idx = -1;
          for (let k = stream.items.length - 1; k >= 0; k -= 1) {
            const it = stream.items[k];
            if (it.kind === 'beat' && it.index === event.beat_index) { idx = k; break; }
          }
          if (idx === -1) {
            stream.items = [...stream.items, {
              kind: 'beat', index: event.beat_index, text: event.delta,
            }];
          } else {
            const copy = [...stream.items];
            copy[idx] = { ...copy[idx], text: copy[idx].text + event.delta };
            stream.items = copy;
          }
          pending.items = stream.items;
          break;
        }
        case 'chart':
          stream.items = [...stream.items, { kind: 'chart', config: event.config }];
          pending.items = stream.items;
          break;
        case 'replay_gap':
          stream.items = [...stream.items, { kind: 'gap' }];
          pending.items = stream.items;
          break;
        default:
          break;
      }
      scheduleFlush();
    };

    try {
      const controller = new AbortController();
      streamAbort.current = { controller, runId: null, transport: 'sse' };
      let done;
      try {
        // SSE by default: plain HTTP through any proxy, and reattach/replay works.
        done = await streamQuery(q, clientSessionId.current, options, {
          onEvent, signal: controller.signal,
        });
      } catch (sseErr) {
        if (sseErr.name === 'AbortError') throw sseErr;
        console.warn('[stream] SSE unavailable, trying WebSocket:', sseErr.message);
        streamAbort.current.transport = 'ws';
        try {
          done = await streamQueryWs(q, clientSessionId.current, options, {
            onEvent, signal: controller.signal,
          });
        } catch (wsErr) {
          if (wsErr.name === 'AbortError') throw wsErr;
          // Neither transport worked (older backend entirely) — take the blocking answer.
          console.warn('[stream] WS unavailable, falling back to POST /api/query:', wsErr.message);
          streamAbort.current.transport = null;
          done = null;
        }
      }
      if (flushTimer !== null) clearTimeout(flushTimer);
      flush();

      if (done) {
        finish({
          question: done.question,
          answer: done.answer,
          success: done.success,
          error: done.error,
          metadata: done.metadata,
        });
      } else {
        finish(await queryAgent(q, clientSessionId.current, options));
      }
    } catch (err) {
      if (flushTimer !== null) clearTimeout(flushTimer);
      if (err.name === 'AbortError') {
        setTurns(prev => prev.map(t =>
          t.id === turnId ? { ...t, loading: false, error: 'Stopped.' } : t
        ));
      } else {
        if (err.message?.includes('401')) { clearToken(); setShowLogin(true); }
        setTurns(prev => prev.map(t =>
          t.id === turnId ? { ...t, loading: false, error: err.message || 'Failed to get response from agent' } : t
        ));
      }
    } finally {
      streamAbort.current = { controller: null, runId: null };
      setLoading(false);
    }
  };

  /** Stop the in-flight query server-side, so it stops spending tokens — not just locally.
   *  Over WebSocket the abort sends a cancel frame on the same connection; SSE has no
   *  client→server channel, so it needs the separate cancel endpoint first. */
  const stopStreaming = async () => {
    const { controller, runId, transport } = streamAbort.current || {};
    if (transport === 'sse' && runId) await cancelStream(runId);
    controller?.abort();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitQuestion(question);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitQuestion(question);
    }
  };

  const newConversation = () => {
    clientSessionId.current = crypto.randomUUID();
    storageSessionId.current = null;
    setActiveConvId(null);
    setTurns([]);
    setQuestion('');
    resetTextarea();
  };

  // Switch to a past conversation: set the session ID and reconstruct turns from stored messages
  const selectConversation = async (convSessionId) => {
    if (convSessionId === storageSessionId.current) return;
    setConvLoading(true);
    try {
      const data = await getConversation(convSessionId);
      storageSessionId.current = convSessionId;
      clientSessionId.current = convSessionId;
      setActiveConvId(convSessionId);

      // Reconstruct turns by pairing human+ai messages
      const msgs = data.messages || [];
      const charts = data.charts || {};
      const reconstructed = [];
      for (let i = 0; i < msgs.length; i += 2) {
        const human = msgs[i];
        const ai = msgs[i + 1];
        const question = human?.content || '';
        const chartConfig = charts[question] || null;
        const mergedMeta = {
          ...(chartConfig ? { chart_config: chartConfig } : {}),
          ...(ai?.metadata || {}),
        };
        reconstructed.push({
          id: i,
          question,
          loading: false,
          result: ai
            ? {
                answer: ai.content,
                success: true,
                metadata: Object.keys(mergedMeta).length ? mergedMeta : null,
                feedback: ai.feedback || null,
              }
            : null,
          error: null,
          // Rebuild the interleaved live view (steps, narration beats, chart position)
          // from the persisted timeline, so a reloaded turn reads like it did at the end
          // of the stream — the "how we got there" is part of the record.
          stream: { items: timelineToItems(mergedMeta.stream_timeline, chartConfig) },
        });
      }
      setTurns(reconstructed);
      setQuestion('');
      resetTextarea();
      setActiveTab('query');
    } catch (err) {
      console.error('Failed to load conversation:', err);
    } finally {
      setConvLoading(false);
    }
  };

  const handleDeleteConversation = async (convSessionId) => {
    try {
      await deleteConversation(convSessionId);
      // If we deleted the active conversation, start fresh
      if (convSessionId === storageSessionId.current) {
        newConversation();
      }
      refreshConversations();
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  const handleRenameConversation = async (convSessionId, title) => {
    try {
      await renameConversation(convSessionId, title);
      refreshConversations();
    } catch (err) {
      console.error('Failed to rename conversation:', err);
    }
  };

  const handleSubmitFeedback = async (turnId, { rating, comment }) => {
    const targetTurn = turns.find(t => t.id === turnId);
    const messageId = targetTurn?.result?.metadata?.message_id;
    if (!messageId || !storageSessionId.current) return;

    setTurns(prev => prev.map(t => (
      t.id === turnId
        ? { ...t, feedbackLoading: true, feedbackError: null }
        : t
    )));

    try {
      const saved = await submitMessageFeedback({
        sessionId: storageSessionId.current,
        messageId,
        rating,
        comment,
      });
      setTurns(prev => prev.map(t => {
        if (t.id !== turnId) return t;
        return {
          ...t,
          feedbackLoading: false,
          feedbackError: null,
          result: {
            ...t.result,
            feedback: {
              message_id: saved.message_id,
              rating: saved.rating,
              comment: saved.comment,
              submitted_at: saved.submitted_at,
            },
          },
        };
      }));
    } catch (err) {
      setTurns(prev => prev.map(t => (
        t.id === turnId
          ? {
              ...t,
              feedbackLoading: false,
              feedbackError: err.message || 'Unknown error',
            }
          : t
      )));
    }
  };

  const hasConversation = turns.length > 0;

  return (
    <div className="app">
      <LoginModal open={showLogin} onSuccess={() => setShowLogin(false)} />

      {/* ── Header ─────────────────────────────────────────── */}
      <header className="header">
        <div className="header-inner">
          <div className="logo-container">
            <img src="/logo.svg" alt="LabV Logo" className="logo" />
          </div>
          <div className="header-text">
            <h1>OpenSearch Natural Language Agent</h1>
            <p className="subtitle">Ask questions about your data</p>
          </div>
        </div>
      </header>

      {/* ── Tab Navigation ─────────────────────────────────── */}
      <div className="tab-navigation">
        {/* History sidebar toggle */}
        <button
          className={`tab-button tab-button--history ${sidebarOpen ? 'active' : ''}`}
          onClick={() => setSidebarOpen(o => !o)}
          title="Toggle conversation history"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" style={{ marginRight: 5, verticalAlign: 'middle' }}>
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
          History
        </button>

        <button
          className={`tab-button ${activeTab === 'query' ? 'active' : ''}`}
          onClick={() => setActiveTab('query')}
        >
          Chat
        </button>
        <button
          className={`tab-button ${activeTab === 'tokens' ? 'active' : ''}`}
          onClick={() => setActiveTab('tokens')}
        >
          Token Usage
        </button>
      </div>

      {/* ── Main ───────────────────────────────────────────── */}
      <main className="main-content">

        {/* Conversation history sidebar */}
        <ConversationSidebar
          open={sidebarOpen}
          conversations={conversations}
          activeId={activeConvId}
          onSelect={selectConversation}
          onDelete={handleDeleteConversation}
          onRename={handleRenameConversation}
          onNew={() => { newConversation(); setSidebarOpen(false); }}
          loading={convLoading}
        />

        {/* Chat Tab */}
        {activeTab === 'query' && (
          <div className="chat-pane">

            {/* Scrollable message area */}
            <div className="chat-messages">
              {!hasConversation && !convLoading && <WelcomeScreen />}
              {convLoading && (
                <div className="conv-loading-overlay">
                  <div className="spinner"></div>
                  <span>Loading conversation…</span>
                </div>
              )}
              {hasConversation && !convLoading && turns.map(turn => (
                <ConversationTurn
                  key={turn.id}
                  turn={turn}
                  onSubmitFeedback={handleSubmitFeedback}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Pinned input bar */}
            <div className="chat-input-bar">

              {/* Example chips — always visible for quick access */}
              <div className="example-chips-wrapper">
                <button className="chips-arrow chips-arrow--left" onClick={() => scrollChips(-1)} tabIndex={-1} aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <div className="example-chips" ref={chipsRef}>
                  {exampleQuestions.map((ex, i) => (
                    <button
                      key={i}
                      className="example-chip"
                      onClick={() => submitQuestion(ex)}
                      disabled={loading}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
                <button className="chips-arrow chips-arrow--right" onClick={() => scrollChips(1)} tabIndex={-1} aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>

              {/* Agent settings row */}
              <div className="agent-settings-row">
                <label className="settings-toggle-label">
                  <span className="settings-label-text">Evaluator</span>
                  <span className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={evaluatorEnabled}
                      onChange={e => setEvaluatorEnabled(e.target.checked)}
                    />
                    <span className="toggle-track">
                      <span className="toggle-thumb" />
                    </span>
                  </span>
                </label>
                <label className={`settings-slider-label ${!evaluatorEnabled ? 'settings-slider-label--disabled' : ''}`}>
                  <span className="settings-label-text">Max Retries</span>
                  <input
                    type="range"
                    className="retries-slider"
                    min={1}
                    max={5}
                    step={1}
                    value={maxRetries}
                    style={{ '--val': maxRetries }}
                    onChange={e => setMaxRetries(Number(e.target.value))}
                    disabled={!evaluatorEnabled}
                  />
                  <span className="retries-value">{maxRetries}</span>
                </label>
              </div>

              {/* Input row */}
              <form onSubmit={handleSubmit} className="input-row">
                <textarea
                  ref={textareaRef}
                  className="chat-textarea"
                  placeholder="Ask a question… (Enter to send, Shift+Enter for new line)"
                  value={question}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  disabled={loading}
                />
                {/* While a query runs, the send button becomes a stop button — a long
                    reasoning run costs real money, so the user needs a way out. */}
                {loading ? (
                  <button
                    type="button"
                    className="send-button send-button--stop"
                    onClick={stopStreaming}
                    title="Stop generating"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="2"></rect>
                    </svg>
                  </button>
                ) : (
                <button
                  type="submit"
                  className="send-button"
                  disabled={!question.trim()}
                  title="Send"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
                )}
              </form>

              {/* Footer row — new conversation button when chat is active */}
              {hasConversation && (
                <div className="input-footer">
                  <button className="btn-new-conversation" onClick={newConversation}>
                    + New Conversation
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Token Usage Tab */}
        {activeTab === 'tokens' && (
          <div className="token-pane">
            <TokenDashboard />
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
